import { isEmpty } from 'radash';
import { useCallback } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { MultiValue, SingleValue } from 'react-select';
import { useShallow } from 'zustand/react/shallow';

import { Combobox } from '@/components/core/Combobox';
import { Image } from '@/components/core/Image';
import { useLayout } from '@/components/datamodel/hooks/useLayout';
import { useDataModel } from '@/hooks/useDataModel';
import { useOrganization } from '@/hooks/useOrganization';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { restUrl } from '@/services/utils';
import { DataModelAttributeTypeName } from '@/types/DataModel';

import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { useOptions } from '../hooks/useOptions';
import { useOptionTranslator } from '../hooks/useOptionTranslator';
import { usePlaceholder } from '../hooks/usePlaceholder';
import { useStableComboboxKey } from '../hooks/useStableComboboxKey';
import { useValidationsStore,Validation } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { EditorComponent } from '../types';

interface EnumComponentProps extends EditorComponent {
    className?: string;
}

const DEFAULT_VALUES_FORMAT = ['label', 'key'];

const EnumComponent = ({
    attribute,
    direction = 'horizontal',
    fieldPath = attribute.name,
    gridMode = false,
    hideLabel,
    readonly= false,
    hidden = false,
    isKeyAttribute = false,
    beforeAttributeControl,
    className,
    labelClassName,
    handleUrlChange
}: EnumComponentProps) => {

    const isSet = attribute.typeName === DataModelAttributeTypeName.EnumSet ||
                    attribute.typeName === DataModelAttributeTypeName.OpenEnumSet ||
                    attribute.typeName === DataModelAttributeTypeName.OpenCodelist ||
                    attribute.typeName === DataModelAttributeTypeName.CodelistSet ||
                    attribute.typeName === DataModelAttributeTypeName.OpenCodelistSet;

    const isOpen = attribute.typeName === DataModelAttributeTypeName.OpenEnum ||
                    attribute.typeName === DataModelAttributeTypeName.OpenEnumSet ||
                    attribute.typeName === DataModelAttributeTypeName.OpenCodelist ||
                    attribute.typeName === DataModelAttributeTypeName.OpenCodelistSet;

    const focusRef = useFocusOnAttribute<HTMLDivElement>(attribute);

    const placeholder = usePlaceholder(attribute, readonly);

    const validationErrors = useValidationsStore(useShallow(state => state.validationErrors[fieldPath] as Validation[]));
    const validationWarnings = useValidationsStore(useShallow(state => state.validationWarnings[fieldPath] as Validation[]));
    const hasErrors = !isEmpty(validationErrors);
    const hasWarnings = !isEmpty(validationWarnings);

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const { control, getValues } = useFormContext();
    const selectedValue = useWatch({
        name: fieldPath,
    });

    const { comboboxKey, markUserChange } = useStableComboboxKey(fieldPath, selectedValue);

    const { dataModel } = useDataModel();
    const { organization } = useOrganization();
    const { layout } = useLayout();
    // FIXME: Make stable
    const currentItem = getValues();

    const { translateOption } = useOptionTranslator();
    const { loading: loadingOptions, options } = useOptions({ attribute, item: currentItem });
    //fallback mechanism for labels
    const mappedOptions:any = options.map(o =>
        ({
            value: o.name || o.key,
            label: o.translatedOption || translateOption(o.name || o.key, attribute, options, o.label) || o.labels?.['en' as keyof typeof o.labels]
        })
    );

    // Map the attribute value to the appropriate option and its translation:
    let mappedValueOrValues: { value: string; label: string; } | { value: string; label: string }[] | undefined;
    if (isSet && !isEmpty(selectedValue)) {

        // In case of sets, we actually need to map multiple values to options:
        const selectedValues = selectedValue as string[];
        mappedValueOrValues = selectedValues.reduce((result, current) => {

            const mappedOption = mappedOptions.find((option:any) => option.value === current);

            if (mappedOption) {
                result.push(mappedOption);
            } else {
                // Always add the selected value even if it's not in the options
                result.push({
                    value: current,
                    label: current
                });
            }

            return result;

        }, [] as { value: string; label: string; }[]);

    } else if (!isEmpty(selectedValue)){
        mappedValueOrValues = mappedOptions.find((option:any) => option.value === selectedValue);

        // If the selected value was not found in the list of options, add it manually
        if (!mappedValueOrValues) {
            mappedValueOrValues = {
                value: selectedValue,
                label: selectedValue,
            };
        }
    }

    const formatSimpleOption = useCallback((
        { value, label }: { value: string, label: string },
        meta?: { context?: 'menu' | 'value' }
    ) => {
        // FIXME: Get the item and pass it to `sectionAttributeParam`
        // FIXME: Get the section where this attribute was rendered in (context?)
        const layoutName = layout?.name || '';
        const section = '';
        let valuesFormat =
            dataModel?.anySectionAttributeParam(layoutName, { attributeStates__: [] }, section, attribute.name, 'valuesFormat') as string[];

        if (!valuesFormat) {
            valuesFormat = DEFAULT_VALUES_FORMAT;
        }
        const hasIcon = valuesFormat.includes('icon');
        const isMenuContext = meta?.context === 'menu';
        const showInOneLine = (gridMode && !isMenuContext);
        const breakText ='tw-whitespace-normal tw-break-words';

        return (
            <div key={value} className={`tw-flex tw-w-full ${ hasIcon ? 'tw-items-center' :'tw-items-baseline'} tw-gap-2`}>
                {valuesFormat.map(valueFormat => {
                    switch(valueFormat) {
                        case 'icon': {
                            // Option icon URLs are stored as translations in the data model translation files.
                            //TOFIX migrate translateOption/ translateOptionIcon to react
                            const $angularJSInjector = getInjector();
                            const $rootScope = getRootScope($angularJSInjector);
                            const iconUrl = $rootScope.translateOptionIcon(value, attribute);
                            if (!iconUrl || !organization?.dataModelGatheringKey) {
                                return null;
                            }
                            const fullUrl = restUrl(`gatherings/${organization.dataModelGatheringKey}/${iconUrl}`);
                            return (
                                <Image key="icon" className={`${readonly ? '!tw-opacity-[50%]': ''} tw-max-w-[20px] tw-inline-block`} alt={value} src={fullUrl} />
                            );

                        }
                        case 'label':
                            return (
                                <div className={`tw-min-w-0 ${ !gridMode && 'tw-max-w-fit'} tw-flex-none ${showInOneLine ? 'tw-w-auto' : '!tw-w-3/5'}`}>
                                    <span data-testid={`label-${label}`} key={`label-${label}`} className={`tw-block tw-text-t-base
                                        ${readonly ? 'tw-text-t-inactive': 'tw-text-t-primary'} ${breakText} `}>
                                        {label}
                                    </span>
                                </div>
                            );
                        case 'key':
                            return (
                                <div className={`tw-min-w-0 ${ !gridMode && 'tw-max-w-fit'} tw-flex-none ${showInOneLine ? 'tw-w-auto' : '!tw-w-2/5'}`}>
                                    <span data-testid={`key-${value}`} key={`key-${value}`} className={`tw-text-t-sm tw-italic
                                        ${readonly ? 'tw-text-t-inactive': 'tw-text-t-primary'} ${breakText}`}>
                                        {value}
                                    </span>
                                </div>
                            );

                    }
                })}
            </div>
        );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridMode, isSet, readonly]);

    /**
     * Convert the Combobox selected value to a value appropriate for the form. i.e. from value/label records to values.
     */
    const mapToFormValue = (newValue: MultiValue<{
        value: string;
        label: string;
    }> | SingleValue<{
        value: string;
        label: string;
    }>) => {
        // Handle null/undefined values (when Combobox is cleared)
        if (newValue === null || newValue === undefined) {
            return isSet ? [] : '';
        }

        if (isSet) {
            const values = newValue as Array<{ value: string, label: string }>;
            return values?.map(v => v.value) || [];
        } else {
            const singleValue = newValue as SingleValue<{ value: string, label: string }>;
            return singleValue?.value || '';
        }

    };

    if (hidden) {
        return null;
    }

    const hasMultipleValues = isSet && Array.isArray(selectedValue) && selectedValue.length >= 1;
    const labelVerticalAlign = hasMultipleValues ? 'top' : undefined;

    return (
        <Layout
            attribute={attribute}
            hideLabel={hideLabel}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={gridMode}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={false}
            labelClassName={labelClassName}
            labelVerticalAlign={labelVerticalAlign}
        >
            <Controller
                control={control}
                name={fieldPath}
                render={({ field }) =>
                    <Combobox
                        key={comboboxKey}
                        className={className}
                        id={fieldPath}
                        inputControlClassnames={`${hasErrors ? 'error-input' : hasWarnings ? 'warning-input' : ''} ${gridMode ? 'tw-bg-white' : 'tw-px-3'}`}
                        isDisabled={readonly}
                        placeholder={placeholder}
                        ref={focusRef}
                        isLoading={loadingOptions}
                        options={mappedOptions}
                        value={mappedValueOrValues}
                        onChange={(newValue) => {
                            const mappedValue = mapToFormValue(newValue);
                            markUserChange(mappedValue);
                            field.onChange(mappedValue);
                            if (!readonly) {
                                requestAnimationFrame(() => focusRef.current?.focus?.());
                            }
                        }}
                        onFocus={handleUrlChange}
                        formatOptionLabel={formatSimpleOption}
                        isMulti={isSet}
                        creatable={isOpen}
                        menuPortalTarget={document.body}
                        isClearable={true}
                        hideDropdownIndicator={gridMode}
                        gridMode={gridMode}
                    />

                }
            />
        </Layout>
    );

};

export { EnumComponent };
